import React, { useState } from 'react';
import { X, Mail, MessageSquare, CheckCircle, Phone, Zap, BarChart2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../utils/analytics';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
}

interface ContactForm {
  email: string;
  message: string;
  website: string;
  selectedServices: string[];
}

const CONTACT_OPTIONS = [
  {
    id: 'call',
    icon: Phone,
    labelKey: 'contact.options.call',
    descKey: 'contact.options.callDesc',
  },
  {
    id: 'quickscan',
    icon: Zap,
    labelKey: 'contact.options.quickscan',
    descKey: 'contact.options.quickscanDesc',
  },
  {
    id: 'demo',
    icon: BarChart2,
    labelKey: 'contact.options.demo',
    descKey: 'contact.options.demoDesc',
  },
];

export function ContactModal({ isOpen, onClose, initialMessage = '' }: ContactModalProps) {
  const { t } = useTranslation();
  const [contactForm, setContactForm] = useState<ContactForm>({
    email: '',
    message: initialMessage,
    website: '',
    selectedServices: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formLoadTime] = useState<number>(Date.now());

  const toggleOption = (id: string) => {
    setContactForm(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(id)
        ? prev.selectedServices.filter(s => s !== id)
        : [...prev.selectedServices, id],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (contactForm.website) {
      toast.error(t('contact.errors.general'));
      return;
    }

    const timeSinceLoad = Date.now() - formLoadTime;
    if (timeSinceLoad < 3000) {
      toast.error(t('contact.errors.slowDown'));
      return;
    }

    if (!contactForm.email || !contactForm.message) {
      toast.error(t('contact.errors.required'));
      return;
    }

    setSubmitting(true);

    try {
      const formData: Record<string, any> = {
        email: contactForm.email,
        message: contactForm.message,
        submissionTime: timeSinceLoad,
        selectedServices: contactForm.selectedServices,
      };

      const functionUrl = `${supabase.supabaseUrl}/functions/v1/send-contact`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabase.supabaseKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: t('contact.errors.general') }));
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || t('contact.errors.general'));
      }

      trackEvent('contact_form_submit', 'engagement', 'success');
      setShowSuccess(true);

      setTimeout(() => {
        setContactForm({ email: '', message: '', website: '', selectedServices: [] });
        setShowSuccess(false);
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.errors.general'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-md w-full mx-4 text-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FFF5E1' }}>
              <CheckCircle className="w-10 h-10" style={{ color: '#F8BD3D' }} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: '#1a1a1a' }}>{t('contact.success.title')}</h3>
            <p style={{ color: '#666666' }}>{t('contact.success.message')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl md:text-2xl font-bold font-sans" style={{ color: '#1a1a1a' }}>
              {t('contact.title')}
            </h2>
            <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
              <X className="w-5 h-5" style={{ color: '#666666' }} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="hidden">
              <input
                type="text"
                name="website"
                value={contactForm.website}
                onChange={(e) => setContactForm(prev => ({ ...prev, website: e.target.value }))}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 font-sans" style={{ color: '#1a1a1a' }}>
                {t('contact.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4" style={{ color: '#F8BD3D' }} />
                </div>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white text-sm focus:outline-none focus:ring-2"
                  style={{
                    border: '1.5px solid #e5e7eb',
                    color: '#1a1a1a',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#F8BD3D')}
                  onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                  placeholder={t('contact.emailPlaceholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-3 font-sans" style={{ color: '#1a1a1a' }}>
                {t('contact.servicesTitle')}
              </label>
              <div className="grid grid-cols-1 gap-3">
                {CONTACT_OPTIONS.map(({ id, icon: Icon, labelKey, descKey }) => {
                  const selected = contactForm.selectedServices.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleOption(id)}
                      className="flex items-center gap-4 p-3.5 rounded-xl text-left transition-all duration-200 w-full"
                      style={{
                        border: selected ? '2px solid #F8BD3D' : '1.5px solid #e5e7eb',
                        backgroundColor: selected ? '#FFF9EC' : '#ffffff',
                      }}
                    >
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: selected ? '#F8BD3D' : '#f3f4f6',
                        }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: selected ? '#1a1a1a' : '#6b7280' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold font-sans" style={{ color: '#1a1a1a' }}>
                          {t(labelKey)}
                        </p>
                        <p className="text-xs mt-0.5 font-sans" style={{ color: '#9ca3af' }}>
                          {t(descKey)}
                        </p>
                      </div>
                      <div
                        className="flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200"
                        style={{
                          borderColor: selected ? '#F8BD3D' : '#d1d5db',
                          backgroundColor: selected ? '#F8BD3D' : 'transparent',
                        }}
                      >
                        {selected && (
                          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs mt-2 font-sans" style={{ color: '#9ca3af' }}>
                {t('contact.servicesOptional')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 font-sans" style={{ color: '#1a1a1a' }}>
                {t('contact.message')}
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <MessageSquare className="h-4 w-4" style={{ color: '#F8BD3D' }} />
                </div>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white text-sm min-h-[100px] resize-none focus:outline-none"
                  style={{
                    border: '1.5px solid #e5e7eb',
                    color: '#1a1a1a',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#F8BD3D')}
                  onBlur={e => (e.target.style.borderColor = '#e5e7eb')}
                  placeholder={t('contact.messagePlaceholder')}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-sans"
              style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#1a1a1a' }}></div>
                  <span>{t('contact.submitting')}</span>
                </>
              ) : (
                t('contact.submit')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
